import getUnicodeFlagIcon from 'country-flag-icons/unicode';
import { useEffect, useMemo, useRef, useState } from 'react';
import ConnectArrowIcon from '../../assets/icons/ConnectArrowIcon';
import DownArrowIcon from '../../assets/icons/DownArrowIcon';
import WarningIcon from '../../assets/icons/WarningIcon';
import { useAppearance } from '../../hooks/useAppearance';
import useClickAway from '../../hooks/useClickAway';
import useLocales from '../../hooks/useLocales';
import { useRouter } from '../../hooks/useRouter';
import { useWalletConnector } from '../../hooks/useWalletConnectors';
import { RoutePath } from '../../pages';
import { ThemeContainer } from '../../styles';
import { ConnectorType } from '../../types';
import { countryCodes } from '../../types/country';
import { getElementDimensionsRelativeToParent, hexToRgba } from '../../utils';
import { defaultCountriesData } from '../../utils/allCountries';
import { validPhoneNumber } from '../../utils/isMobilePhone';
import Button from '../button';
import { StyleDropdownWrapper, StyleHoverBg } from '../chainDropdown/styles';
import CircleSpinner from '../circleSpinner';
import Dropdown from '../dropdown';
import { StyleConnectArrowIcon, StyleEmailInputWrapper, StyleInputError } from '../emailLogin/styles';
import {
  StyleAccountSelectCountryItem,
  StyleAccountSelectCountryList,
  StyleAccountSelectOpt,
  StyleCountryBox,
  StyleCountryCode,
  StyleCountryFlag,
  StyleCountryItem,
  StyleCountryName,
  StyleDownMore,
  StyleNoData,
  StylePhoneInputContainer,
  StylePhoneInputItemContainer,
  StylePhoneLabel,
  StylePhoneLoginContainer,
  StylePrefixWrap,
  StyleSearchInput,
} from './styles';

const PhoneInputItem = () => {
  const { appearance } = useAppearance();
  const { navigate } = useRouter();
  const locales = useLocales();

  const [countryData, setCountryData] = useState<string[]>([]);
  const [phoneValue, setPhoneValue] = useState('');
  const [visbSelectCountry, setVisbSelectCountry] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [phoneError, setPhoneError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [listPosition, setListPosition] = useState<'top' | 'bottom'>('bottom');
  const [listMaxHeight, setListMaxHeight] = useState<number>(300);
  const [hoverIndex, setHoverIndex] = useState(-1);
  const [inputFocus, setInputFocus] = useState<boolean>(false);

  const clickRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const countryListWrapRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const phoneContainerRef = useRef<HTMLDivElement>(null);
  const timer = useRef<NodeJS.Timeout>(undefined);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const walletConnector = useWalletConnector('particleAuth');

  const sendCode = async () => {
    try {
      if (phoneValue) {
        const phoneLocale = countryData[1];
        const countryCode = countryData[2];
        if (!countryCode) {
          throw new Error('Please select a country code');
        } else if (!phoneValue) {
          throw new Error(locales.enterPhoneNumber);
        } else if (validPhoneNumber(phoneLocale, countryCode, phoneValue, locales)) {
          setLoading(true);

          const token = await walletConnector?.auth.getCloudflareTurnstileResponse({
            // @ts-ignore
            theme: appearance.mode || 'lgiht',
            getContainer: footerRef.current as HTMLElement,
            language: appearance.language || 'en',
          });

          console.log('token', token);

          // 2. 发送验证码
          await walletConnector?.auth.getConnectCaptcha({
            phone: `+${countryCode}${phoneValue}`,
            // eslint-disable-next-line camelcase
            cf_turnstile_response: token,
          });

          // 3. 跳转到验证码输入页面
          navigate(RoutePath.Captcha, {
            value: `+${countryCode}${phoneValue}`,
            type: ConnectorType.Phone,
          });
        }
      }
    } catch (error: any) {
      console.log('sendCode error', error);
      let message = error?.extra?.[0] || error.message || (typeof error === 'string' ? error : locales.networkError);
      if (message.includes('phone must be a valid')) {
        message = locales.invalidOrIncorrectNumberMistype;
      } else if (message.includes('Network Error')) {
        message = locales.networkError;
      }
      setPhoneError(message);
    }
    setLoading(false);
  };

  const countriesList = useMemo(() => {
    let filterCountryCodes = countryCodes;

    if (appearance?.filterCountryCallingCode) {
      filterCountryCodes = appearance?.filterCountryCallingCode(countryCodes);
    }

    if (!filterCountryCodes?.length) {
      console.log('filterCountryCallingCode is empty');
    }

    return filterCountryCodes
      .map((countryCode) => {
        return defaultCountriesData.find((item) => item[1]?.toLowerCase() === countryCode?.toLowerCase());
      })
      .filter((item) => !!item);
  }, []);

  const searclCountriesList = useMemo(() => {
    return countriesList.filter((item) => {
      return (item as string[]).join('+').toLowerCase().includes(searchValue.toLowerCase());
    });
  }, [countriesList, searchValue]);

  /**
   * hide continue button
   */
  const hideContinueButton = useMemo(() => {
    return appearance?.hideContinueButton;
  }, [appearance]);

  /**
   * 监听输入 和 选择的国家码，如果校验不通过就返回true，否则就返回false
   */
  const disabled = useMemo(() => {
    try {
      if (!countryData[1] || !countryData[2] || !phoneValue) {
        return true;
      }
      const isValid = validPhoneNumber(countryData[1], countryData[2], phoneValue, locales);
      return !isValid;
    } catch (error) {
      // eslint-disable-next-line no-console
    }
    return true;
  }, [countryData, phoneValue, locales]);

  const hoverBgColor = useMemo(() => {
    const color = appearance.theme?.['--pcm-accent-color'] || '#A257FA';
    return hexToRgba(color, 0.1);
  }, [appearance]);

  useClickAway(() => {
    setVisbSelectCountry(false);
  }, [clickRef, countryListWrapRef]);

  useEffect(() => {
    if (visbSelectCountry) {
      setSearchValue('');
    }
  }, [visbSelectCountry]);

  /**
   * set default country
   */
  useEffect(() => {
    const language = navigator.languages && navigator.languages.length ? navigator.languages[0] : navigator.language;
    const locales = language?.split('-');
    const region = (locales || []).length > 1 ? locales?.[1] : locales?.[0];
    if (countriesList.length) {
      if (region && region.length > 0) {
        const currentCountry = defaultCountriesData.find((item) => item?.[1]?.toUpperCase() === region) || [];
        if (
          currentCountry &&
          countriesList.find((item: any) => item[1]?.toLowerCase() === currentCountry?.[1]?.toLowerCase())
        ) {
          setCountryData(currentCountry);
        } else {
          setCountryData(countriesList[0] as string[]);
        }
      }
    }
  }, [countriesList]);

  useEffect(() => {
    const { topDistance, bottomDistance } = getElementDimensionsRelativeToParent(phoneContainerRef) || {};
    if ((bottomDistance && topDistance && bottomDistance > topDistance) || (bottomDistance || 0) > 200) {
      setListPosition('bottom');
      const maxHeight = (bottomDistance || 0) + 40;
      setListMaxHeight(maxHeight > 300 ? 300 : maxHeight);
    } else {
      setListPosition('top');
      const maxHeight = (topDistance || 0) - 10;
      setListMaxHeight(maxHeight > 300 ? 300 : maxHeight);
    }
  }, []);

  return (
    <StylePhoneLoginContainer ref={phoneContainerRef}>
      <StyleEmailInputWrapper>
        <StylePhoneInputContainer error={phoneError} $inputFocus={inputFocus}>
          <StylePhoneInputItemContainer>
            <StyleCountryBox>
              <StylePrefixWrap>
                <Dropdown
                  ref={dropdownRef}
                  onChange={() => {
                    setSearchValue('');
                    setHoverIndex(-1);
                  }}
                  trigger={
                    <StyleAccountSelectOpt>
                      <div>{getUnicodeFlagIcon(`${countryData[1]}`)}</div>
                      <StyleDownMore>
                        <DownArrowIcon />
                      </StyleDownMore>
                    </StyleAccountSelectOpt>
                  }
                  content={
                    <ThemeContainer $useTheme={appearance?.theme} $useMode={appearance?.mode} suppressHydrationWarning>
                      <StyleAccountSelectCountryList
                        ref={countryListWrapRef}
                        listposition={listPosition}
                        $maxHeight={listMaxHeight}
                      >
                        {countriesList.length > 6 && (
                          <StyleAccountSelectCountryItem
                            className='search-input-wrap'
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <div className='wrap'>
                              <StyleSearchInput
                                ref={searchInputRef}
                                autoComplete='off'
                                type='text'
                                placeholder={locales.searchByNameOrCode}
                                onChange={(e) => {
                                  const value = e?.target?.value || '';
                                  setSearchValue(value);
                                }}
                              />
                            </div>
                          </StyleAccountSelectCountryItem>
                        )}

                        {searclCountriesList && !!searclCountriesList.length ? (
                          <StyleDropdownWrapper
                            style={{
                              flex: 1,
                              overflowY: 'auto',
                              maxHeight: '280px',
                            }}
                          >
                            <StyleHoverBg index={hoverIndex} $source='countryList' />
                            {searclCountriesList.map((item: any, index) => (
                              <StyleAccountSelectCountryItem
                                key={`${item[0]}-${item[1]}-${item[2]}`}
                                onClick={() => {
                                  setCountryData(item);
                                  setPhoneError('');
                                  setVisbSelectCountry(false);
                                  // @ts-ignore
                                  dropdownRef?.current?.toggleDropdown?.();
                                }}
                                $hoverBgColor={hoverBgColor}
                                onMouseEnter={() => {
                                  clearTimeout(timer.current);
                                  setHoverIndex(index);
                                }}
                                onMouseLeave={() => {
                                  timer.current = setTimeout(() => {
                                    setHoverIndex(-1);
                                  }, 100);
                                }}
                              >
                                <StyleCountryItem>
                                  <StyleCountryFlag>{getUnicodeFlagIcon(`${item[1]}`)}</StyleCountryFlag>
                                  <StyleCountryName>{item[0]}</StyleCountryName>
                                </StyleCountryItem>
                                <StyleCountryCode>{'+' + item[2]}</StyleCountryCode>
                              </StyleAccountSelectCountryItem>
                            ))}
                          </StyleDropdownWrapper>
                        ) : (
                          <StyleNoData>{locales.noData}</StyleNoData>
                        )}
                      </StyleAccountSelectCountryList>
                    </ThemeContainer>
                  }
                  offsetY={20}
                  dropdownWidth={(phoneContainerRef?.current?.clientWidth || 300) - 2}
                  dropdownHeight={300}
                  offsetX={0}
                />
                <span>+{countryData[2]}</span>
              </StylePrefixWrap>
            </StyleCountryBox>
            <StylePhoneLabel error={phoneError}>
              <input
                placeholder={locales.phoneNumber}
                onChange={(value) => {
                  setPhoneValue(value.target.value?.trim() || '');
                  setPhoneError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !disabled) {
                    e.preventDefault();
                    sendCode();
                  }
                }}
                onFocus={() => {
                  setInputFocus(true);
                }}
                onBlur={() => {
                  setInputFocus(false);
                }}
              />
            </StylePhoneLabel>
          </StylePhoneInputItemContainer>
        </StylePhoneInputContainer>
        {hideContinueButton && (
          <StyleConnectArrowIcon
            onClick={() => {
              if (!loading && !disabled) {
                sendCode();
              }
            }}
            disabled={disabled}
            $canClick={phoneValue?.length >= 1}
            $isError={!!phoneError}
          >
            <CircleSpinner
              logo={
                <div className='icon-0x7ff'>
                  <ConnectArrowIcon />
                </div>
              }
              connecting={loading}
              inset={0}
              width={30}
              height={30}
            />
          </StyleConnectArrowIcon>
        )}
      </StyleEmailInputWrapper>
      {phoneError && (
        <StyleInputError>
          <div>
            <WarningIcon />
          </div>
          <div>{phoneError}</div>
        </StyleInputError>
      )}
      <div ref={footerRef}>
        {!hideContinueButton && (
          <Button style={{ marginTop: 16 }} onClick={sendCode} loading={loading} disabled={disabled}>
            {locales.continue}
          </Button>
        )}
      </div>
    </StylePhoneLoginContainer>
  );
};

export default PhoneInputItem;
